import { INVOICE_STATUS_META } from '@/constants'
import {
  IInvoiceDetailsFacility,
  IInvoiceDetailsMasterNode,
} from '@/types/billing.interface'
import { formatPrice } from '@/utils/formatPrice'
import {
  HStack,
  Text,
  Badge,
  Divider,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { format } from 'date-fns'
import { useTranslations } from 'next-intl'
import PaymentStatusAlert from '@/features/payments/PaymentStatusAlert'

interface BillingSummaryProps {
  facilities: IInvoiceDetailsFacility[]
  details: IInvoiceDetailsMasterNode[]
  isDrawer?: boolean
}

export const BillingSummary = ({
  facilities,
  details,
  isDrawer = false,
}: BillingSummaryProps) => {
  const t = useTranslations()
  const blackAlpha900White = useColorModeValue('blackAlpha.900', 'white')
  const blackAlpha800 = useColorModeValue('blackAlpha.800', 'white')
  const alpha100 = useColorModeValue('blackAlpha.100', 'whiteAlpha.100')

  const lateFee = formatPrice(
    details?.reduce((acc, d) => acc + (Number(d.totalLateFee) || 0), 0)
  )
  const taxes = formatPrice(
    details?.reduce((acc, d) => acc + (Number(d.totalTaxes) || 0), 0)
  )

  const totalCreditsApplied = details?.reduce(
    (acc, d) => acc + (Number(d.totalCreditsApplied) || 0),
    0
  )

  const totalWalletApplied = details?.reduce(
    (acc, d) => acc + (Number(d.totalWalletApplied) || 0),
    0
  )

  const subTotal = details?.reduce(
    (acc, d) => acc + (Number(d.subtotal) || 0),
    0
  )

  const isPendingAndCrypto = details.some(
    (detail) =>
      detail?.invoiceStatus === 'PENDING' &&
      detail?.invoicePaymentName === 'opennode:crypto'
  )

  const repairSummary = details.flatMap((detail) => {
    if (
      detail.hardwareInvoiceType === 'REPAIR' &&
      detail?.repairBreakdown?.length
    ) {
      return detail.repairBreakdown.map((repair) => ({
        shortId: repair.shortId,
        total: repair.invoicedTotalCustomerFee,
      }))
    }
    return []
  })

  const FALLBACK = {
    label: '',
    color: 'gray.600',
    bgColor: 'gray.100',
  }
  const meta =
    INVOICE_STATUS_META[details && details[0]?.invoiceStatus] ?? FALLBACK

  return (
    <>
      <HStack justify="space-between" mb={4}>
        <Text fontSize="18px" fontWeight="600">
          {t('dashboard.billing.invoice-summary')}
        </Text>
        <Badge
          bg={meta.bgColor}
          color={meta.color}
          size="md"
          py={1}
          px={2}
          borderRadius="6px"
          textTransform="capitalize"
        >
          {meta.label && t(meta.label)}
        </Badge>
      </HStack>

      <Divider bg={alpha100} />

      {isPendingAndCrypto && (
        <PaymentStatusAlert status="IN_PROCESS" backToInvoices={false} />
      )}

      <VStack spacing={4} align="stretch" my={4}>
        <HStack justify="space-between">
          <Text fontSize="14px" color={blackAlpha800} fontWeight={400}>
            {t('dashboard.billing.due-date')}
          </Text>
          <Text fontSize="16px" fontWeight="600" color={blackAlpha900White}>
            {details && details[0]?.invoiceDueDate
              ? format(new Date(details[0]?.invoiceDueDate), 'MMM do, yyyy')
              : null}
          </Text>
        </HStack>
        {facilities &&
          facilities?.map(
            (facility: IInvoiceDetailsFacility, index: number) => (
              <HStack
                key={`facility-${facility.facilityName}-${index}`}
                justify="space-between"
              >
                <Text
                  fontSize="14px"
                  color={blackAlpha800}
                  fontWeight={400}
                >{`${t('dashboard.miners.total-miners')}: ${
                  facility.facilityName
                }`}</Text>
                <Text
                  fontSize="16px"
                  fontWeight="600"
                  color={blackAlpha900White}
                >
                  {facility.miners.length}
                </Text>
              </HStack>
            )
          )}

        {lateFee !== '$0.00' && (
          <HStack justify="space-between">
            <Text fontSize="14px" color={blackAlpha800} fontWeight={400}>
              {t('dashboard.billing.late-fee')}
            </Text>
            <Text fontSize="16px" fontWeight="600" color={blackAlpha900White}>
              {lateFee}
            </Text>
          </HStack>
        )}
        {taxes !== '$0.00' && (
          <HStack justify="space-between">
            <Text fontSize="14px" color={blackAlpha800} fontWeight={400}>
              {t('dashboard.billing.taxes')}
            </Text>
            <Text fontSize="16px" fontWeight="600" color={blackAlpha900White}>
              {taxes}
            </Text>
          </HStack>
        )}
      </VStack>

      <Divider bg={alpha100} />

      {!isDrawer && repairSummary.length ? (
        <>
          <VStack spacing={4} align="stretch" my={4}>
            {repairSummary.map((repair) => (
              <HStack justify="space-between" key={repair.shortId}>
                <Text fontSize="14px" color={blackAlpha800} fontWeight={400}>
                  {repair.shortId}
                </Text>
                <Text
                  fontSize="16px"
                  fontWeight="600"
                  color={blackAlpha900White}
                >
                  {formatPrice(repair.total)}
                </Text>
              </HStack>
            ))}
          </VStack>
          <Divider bg={alpha100} />
        </>
      ) : null}

      <VStack spacing={4} align="stretch" my={4}>
        <HStack justify="space-between">
          <Text fontSize="14px" color={blackAlpha800} fontWeight={400}>
            {t('common.sub-total')}
          </Text>
          <Text fontSize="16px" fontWeight="600" color={blackAlpha900White}>
            {formatPrice(subTotal)}
          </Text>
        </HStack>
        {details?.some((d) => d.totalCreditsApplied != '0.00') && (
          <HStack justify="space-between">
            <Text fontSize="14px" color={blackAlpha800} fontWeight={400}>
              {t('dashboard.billing.applied-compass-credits')}
            </Text>
            <Text fontSize="16px" fontWeight="600" color="brandGreen.50">
              - {formatPrice(totalCreditsApplied)}
            </Text>
          </HStack>
        )}
        {details?.some((d) => d.totalWalletApplied != '0.00') && (
          <HStack justify="space-between">
            <Text fontSize="14px" color={blackAlpha800} fontWeight={400}>
              {t('dashboard.billing.applied-wallet-funds')}
            </Text>
            <Text fontSize="16px" fontWeight="600" color="brandGreen.50">
              - {formatPrice(totalWalletApplied)}
            </Text>
          </HStack>
        )}
      </VStack>
    </>
  )
}
